import React from "react";
import PropTypes from "prop-types";
import { isNil } from "lodash";
import { matchPath, useLocation } from "react-router-dom";

import WorkspaceChallengeLoading from "./WorkspaceChallengeLoading";
import ErrorPage from "src/components/ErrorPage";
import {
  isWorkspaceNotFoundError,
  ERROR_CODES,
} from "src/components/ErrorPage/detectErrorType";
import CreateWorkspacePage from "src/pages/Workspaces/CreateWorkspacePage";
import {
  useGlobalContextError,
  useIsContextReady,
} from "src/contexts/global/GlobalContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import ErrorMessage from "src/components/ErrorPage/ErrorMessage";
import { BAMBOOHR_OAUTH_CALLBACK_PATH } from "src/integrationTypes/components/bambooHROAuth";

import {
  useIsWorkspaceLoading,
  useWorkspaceChallenge,
  useWorkspaceError,
  CHALLENGE_SELECT_WORKSPACE,
  CHALLENGE_SETUP_WORKSPACE,
} from "./WorkspaceContext";

const WorkspaceChallenge = ({ children }) => {
  const location = useLocation();
  const isWorkspaceLoading = useIsWorkspaceLoading();
  const challenge = useWorkspaceChallenge();
  const workspaceError = useWorkspaceError();
  const globalContextError = useGlobalContextError();
  const isContextReady = useIsContextReady();
  const { user } = useAuth0() || {};

  const isBambooOAuthCallback = Boolean(
    matchPath(location.pathname, {
      path: BAMBOOHR_OAUTH_CALLBACK_PATH,
      exact: true,
    })
  );

  if (isWorkspaceLoading || isNil(challenge)) {
    return <WorkspaceChallengeLoading waitingFor="getWorkspace" />;
  }

  // Workspace-less OAuth return URL: carry workspace via OAuth state / pending
  // session storage + x-workspace-id on the token exchange. Do not block on
  // active-workspace selection or getContext (those need a URL workspace).
  if (isBambooOAuthCallback) {
    return children;
  }

  if (isWorkspaceNotFoundError(workspaceError)) {
    throw workspaceError;
  }
  if (workspaceError) {
    return <ErrorPage error={workspaceError} />;
  }
  if (challenge === CHALLENGE_SELECT_WORKSPACE) {
    throw new Error(ERROR_CODES.WORKSPACE_ACCESS_DENIED);
  }
  if (challenge === CHALLENGE_SETUP_WORKSPACE) {
    return <CreateWorkspacePage />;
  }

  // Evaluate Workspace Errors before showing context errors
  if (globalContextError) {
    return <ErrorMessage error={globalContextError} user={user} />;
  }
  if (!isContextReady) {
    return <WorkspaceChallengeLoading waitingFor="getContextQuery" />;
  }

  return children;
};

WorkspaceChallenge.propTypes = {
  children: PropTypes.node.isRequired,
};

export default WorkspaceChallenge;
